import { supabase } from '@turnifly/shared/api'
import type {
  PomodoroSettings,
  PomodoroTask,
  PomodoroStats,
  PomodoroPreset,
} from '@turnifly/shared/types'
import {
  PomodoroSettingsSchema,
  PomodoroTaskSchema,
  PomodoroStatsSchema,
  PomodoroPresetSchema,
} from '@turnifly/shared/types'
import { z } from 'zod'

// ── Settings ──

export async function fetchPomodoroSettings(userId: string): Promise<PomodoroSettings | null> {
  const { data } = await supabase
    .from('pomodoro_settings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
    .throwOnError()

  if (!data) return null
  return PomodoroSettingsSchema.parse(data)
}

export async function upsertPomodoroSettings(
  userId: string,
  settings: Partial<PomodoroSettings>,
): Promise<PomodoroSettings> {
  const { data } = await supabase
    .from('pomodoro_settings')
    .upsert({ user_id: userId, ...settings }, { onConflict: 'user_id' })
    .select('*')
    .single()
    .throwOnError()

  return PomodoroSettingsSchema.parse(data)
}

// ── Sessions ──

interface CreateSessionInput {
  userId: string
  sessionType: 'work' | 'short_break' | 'long_break'
  plannedMinutes: number
  actualSeconds: number
  habitId?: string | null
  taskText?: string | null
  pomodoroTaskId?: string | null
  completed: boolean
  startedAt: string
}

export async function createPomodoroSession(input: CreateSessionInput): Promise<void> {
  await supabase
    .from('pomodoro_sessions')
    .insert({
      user_id: input.userId,
      session_type: input.sessionType,
      planned_minutes: input.plannedMinutes,
      actual_seconds: input.actualSeconds,
      habit_id: input.habitId ?? null,
      task_text: input.taskText ?? null,
      pomodoro_task_id: input.pomodoroTaskId ?? null,
      completed: input.completed,
      started_at: input.startedAt,
      ended_at: new Date().toISOString(),
    })
    .throwOnError()
}

// ── Stats ──

export async function fetchPomodoroStats(userId: string): Promise<PomodoroStats> {
  const { data } = await supabase
    .rpc('get_pomodoro_stats', { p_user_id: userId })
    .throwOnError()

  return PomodoroStatsSchema.parse(data)
}

// ── Tasks ──

export async function fetchPomodoroTasks(userId: string): Promise<PomodoroTask[]> {
  const { data } = await supabase
    .from('pomodoro_tasks')
    .select('*')
    .eq('user_id', userId)
    .order('sort_order')
    .order('created_at')
    .throwOnError()

  return z.array(PomodoroTaskSchema).parse(data ?? [])
}

export async function createPomodoroTask(
  userId: string,
  text: string,
  targetPomodoros: number = 1,
  habitId?: string | null,
): Promise<PomodoroTask> {
  const { data } = await supabase
    .from('pomodoro_tasks')
    .insert({
      user_id: userId,
      text,
      target_pomodoros: Math.min(Math.max(targetPomodoros, 1), 20),
      habit_id: habitId ?? null,
    })
    .select('*')
    .single()
    .throwOnError()

  return PomodoroTaskSchema.parse(data)
}

export async function togglePomodoroTask(taskId: string, completed: boolean): Promise<void> {
  await supabase
    .from('pomodoro_tasks')
    .update({ completed })
    .eq('id', taskId)
    .throwOnError()
}

export async function deletePomodoroTask(taskId: string): Promise<void> {
  await supabase
    .from('pomodoro_tasks')
    .delete()
    .eq('id', taskId)
    .throwOnError()
}

// ── Post completion (user chooses "Ha" in modal) ──

export async function createPomodoroCompletionPost(
  userId: string,
  pomodoroTaskId: string,
): Promise<void> {
  await supabase
    .rpc('create_pomodoro_completion_post', {
      p_user_id: userId,
      p_pomodoro_task_id: pomodoroTaskId,
    })
    .throwOnError()
}

export async function clearCompletedPomodoroTasks(userId: string): Promise<void> {
  await supabase
    .from('pomodoro_tasks')
    .delete()
    .eq('user_id', userId)
    .eq('completed', true)
    .throwOnError()
}

// ── Presets ──

export async function fetchPomodoroPresets(): Promise<PomodoroPreset[]> {
  const { data } = await supabase
    .from('pomodoro_presets')
    .select('*')
    .order('sort_order')
    .throwOnError()

  return z.array(PomodoroPresetSchema).parse(data ?? [])
}
